import { useMemo } from 'react';
import {
  Area, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import type { ForecastPoint, ForecastMeta } from '@shared/types';
import { CHART, tooltipStyle } from './chartTheme';

const LEGEND = [
  { label: 'Demanda real (vendida + no atendida)', color: CHART.historical, dashed: false },
  { label: 'Proyección', color: CHART.base, dashed: false },
  { label: 'Banda de confianza', color: CHART.base, dashed: false, band: true },
];
// Los escenarios optimista/pesimista eran ±22 % fijos sobre una curva inventada. El motor no
// produce escenarios todavía, así que las dos líneas quedan fuera hasta que existan de verdad.

/** Demand projection with 95% confidence band + scenario envelopes.
 *  `horizon` (30/60/90) limits how far past "hoy" is shown. */
export function DemandForecastChart({
  points,
  meta,
  horizon = 60,
  height = 320,
}: {
  points: ForecastPoint[];
  meta: ForecastMeta;
  horizon?: number;
  height?: number;
}) {
  const data = useMemo(() => {
    const todayIdx = points.findIndex((p) => p.date === meta.todayLabel);
    const cutoff = todayIdx >= 0 ? todayIdx + horizon : points.length;
    return points.slice(0, cutoff + 1).map((p) => ({
      ...p,
      band: p.upper != null && p.lower != null ? [p.lower, p.upper] : undefined,
    }));
  }, [points, meta.todayLabel, horizon]);

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 16, right: 12, left: -8, bottom: 0 }}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: CHART.axis, fontSize: CHART.axisFont, fontFamily: CHART.fontFamily }}
            tickLine={false}
            axisLine={{ stroke: CHART.grid }}
            interval={Math.max(1, Math.floor(data.length / 14))}
            minTickGap={16}
          />
          <YAxis
            tick={{ fill: CHART.axis, fontSize: CHART.axisFont, fontFamily: CHART.fontFamily }}
            tickLine={false}
            axisLine={false}
            width={52}
            // Antes estaba fijo en [0, 800] — el techo de la curva sembrada. Con datos reales
            // recortaba el gráfico sin avisar. Y con miles de unidades la etiqueta no entraba
            // en 44 px: salía "4000" recortado a ".4000". Por eso van en miles.
            domain={[0, 'auto']}
            tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
          />
          <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#9CA3AF' }} />
          <Area dataKey="band" stroke="none" fill={CHART.band} isAnimationActive={false} connectNulls />
          <ReferenceLine
            x={meta.todayLabel}
            stroke={CHART.axis}
            strokeDasharray="3 3"
            label={{ value: 'Hoy', position: 'top', fill: CHART.axis, fontSize: 11 }}
          />
          <Line dataKey="historical" stroke={CHART.historical} strokeWidth={2} dot={false} connectNulls />
          <Line dataKey="base" stroke={CHART.base} strokeWidth={2.5} dot={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {LEGEND.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5 text-2xs text-ink-muted">
            <span
              className="inline-block h-2.5 w-4 rounded-sm"
              style={{
                background: l.band ? CHART.band : 'transparent',
                borderTop: l.band ? 'none' : `2px ${l.dashed ? 'dashed' : 'solid'} ${l.color}`,
              }}
            />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}
